// --- Dependencies --------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import useStoreLanguages from "@/hooks/useStoreLanguages";
import useTranslation from "@/hooks/useTranslation";
import type { TyApiResponse, TyCategory, TyCategoryDetail } from "@/types";
import MultiLangInput from "@/UI/inputs/MultiLangInput";
import { hasAnyLangValue, readLangs, writeLangs } from "@/utils";
import type { TyLangValues } from "@/utils";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

export type FormCategoryPopupDetails = {
    category?: TyCategory;
    onSuccess: () => void;
};

type Props = {
    onClose: () => void;
    details?: FormCategoryPopupDetails;
};

// --- Logic --------------------------------------------------------------------------------------

export default function FormCategoryPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const { languages, defaultLang } = useStoreLanguages();
    const isEdit = Boolean(details?.category);

    const [name, setName] = useState<TyLangValues>({});

    // Fetch raw category data (edit mode only) ---------------------------------------------------

    const { data: detailData, loading: detailLoading } = useFetch<TyApiResponse<{ category: TyCategoryDetail }>>(
        `/menu/categories/${details?.category?._id ?? "x"}`,
        {},
        isEdit
    );

    useEffect(() => {
        if (!detailData?.data?.category) return;
        setName(readLangs(detailData.data.category.name, languages));
    }, [detailData]);

    // Submit -------------------------------------------------------------------------------------

    const submitPath = isEdit ? `/menu/categories/${details!.category!._id}` : "/menu/categories";
    const submitMethod = isEdit ? "PUT" : "POST";

    const {
        data: submitData,
        loading: submitLoading,
        error,
        invoke,
    } = useFetch<TyApiResponse<{ category: TyCategory }>>(submitPath, { method: submitMethod }, false);

    useEffect(() => {
        if (!submitData?.success) return;
        details?.onSuccess();
        onClose();
    }, [submitData]);

    // --------------------------------------------------------------------------------------------

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const entries = writeLangs(name, languages, defaultLang);
        if (entries.length === 0) return;
        invoke({ data: { name: entries } });
    };

    const isLoading = detailLoading || submitLoading;
    const isValid = hasAnyLangValue(name) && !detailLoading;

    return (
        <div className="form-category-popup">
            <div className="form-category-popup-header">
                <p className="form-category-popup-title">{isEdit ? t.formCategory.editTitle : t.formCategory.createTitle}</p>
                <button type="button" className="form-category-popup-close" onClick={onClose} aria-label="Close">
                    <img src="/icons/close-icon.svg" alt="" width={16} height={16} />
                </button>
            </div>
            <form className="form-category-popup-body" onSubmit={handleSubmit}>
                <MultiLangInput
                    label={t.formCategory.nameLabel}
                    languages={languages}
                    values={name}
                    onChange={(lang, value) => setName((prev) => ({ ...prev, [lang]: value }))}
                    disabled={isLoading}
                />
                {error && <p className="form-category-popup-error">{error}</p>}
                <button type="submit" className="form-category-popup-submit" disabled={!isValid || isLoading}>
                    {isEdit ? t.formCategory.submitEdit : t.formCategory.submitCreate}
                </button>
            </form>
        </div>
    );
}
